import React, {useState, useCallback, useRef, useEffect} from 'react';
import styled, {keyframes} from 'styled-components';
import ColorPicker from 'react-best-gradient-color-picker';
import {playColorSelectSound} from '../utils/sounds';
import {useVibration} from '../hooks/useVibration';
import {FOOTER_HEIGHT} from '../styles/constants';
import {ColorScheme} from "../types";
import {PersistentManager} from "../utils/PersistentManager";
import {useIsMobile} from "../hooks/useIsMobile";
import { trackColorSelection, trackUserInteraction } from '../utils/analytics';

interface StyledProps {
    isMobile?: boolean;
}

const PaletteContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
    width: fit-content;
    max-width: 100%;
    align-items: center;
    justify-content: center;
    justify-items: center;
  padding: 8px;
  background: white;
  overflow: hidden;
`;

const ColorRow = styled.div<StyledProps>`
  display: flex;
  flex-direction: row;
  justify-content: ${props => props.isMobile ? 'space-evenly' : 'center'};
  gap: 6px;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 8px 4px;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
  white-space: nowrap;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 4px;
  }
  
  /* Handle on hover */
  &::-webkit-scrollbar-thumb:hover {
    background: #bbb;
  }
`;

interface ColorButtonProps {
    color: string;
    isSelected: boolean;
    isEditMode: boolean;
    isCompleted?: boolean;
}

const ColorButtonWrapper = styled.div`
  position: relative;
  flex: 0 0 auto;
  display: inline-block;
`;

const ColorButton = styled.button<ColorButtonProps>`
  aspect-ratio: 1;
  width: 2em;
  height: 2em;
  background-color: ${props => props.color};
  border-radius: ${props => (props.isSelected ? '50%' : '4px')};
  border: ${props => props.isEditMode ? '2px dashed black' : 'none'};
  cursor: ${props => props.isCompleted ? 'default' : 'pointer'};
  padding: 0;
  transition: transform 0.2s, border-radius 0.2s, opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  opacity: ${props => props.isCompleted ? '0.5' : '1'};

    &.completed {
        opacity: 0.4;
    }
    
  &:hover {
    transform: ${props => props.isCompleted ? 'none' : 'scale(1.1)'};
  }
`;

const ColorActionButton = styled(ColorButton)`
  background-color: transparent;
  border-radius: 0;
  border: ${props => props.isSelected ? '2px dashed black' : 'none'};
  font-size: 1.4rem;
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.6);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const SelectedOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
  font-weight: bold;
  color: white;
  text-shadow: 0px 0px 2px black, 0px 0px 3px black;
  pointer-events: none;
  animation: ${fadeIn} 0.3s ease-out;
`;

const ColorPickerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: ${FOOTER_HEIGHT + 10}px;
  background: white;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  padding: 16px;
`;

const ColorPickerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ColorPickerTitle = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  color: #333;
`;

const ColorPickerButtonsPanel = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 8px;
  color: #666;

  &:hover {
    color: #000;
  }
`;

const ColorPickerContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;

  .color-picker {
    width: 100% !important;
    max-width: 400px;
  }
`;

interface ColorPaletteProps {
    selectedColor: string;
    onColorSelect: (color: string, id: string) => void;
    colorScheme?: ColorScheme;
    completedColors?: { [colorIdx: string]: boolean };
}

const STORAGE_KEY = 'colorPaletteColors';
const PLAY_SOUND = false; // Sound setting for color selection

const defaultColors = [
    '#FF0000', '#FF4500', '#FFA500', '#FFFF00', '#90EE90', '#008000', '#00BFFF', '#0000FF', '#4B0082', '#800080', '#FF69B4', '#FFC0CB',
    '#800000', '#8B4513', '#556B2F', '#006400', '#000080', '#483D8B', '#4B0082', '#8B008B', '#BC8F8F', '#A0522D', '#696969', '#000000',
    '#FFB6C1', '#FFA07A', '#F0E68C', '#98FB98', '#87CEEB', '#B0C4DE', '#DDA0DD', '#FFE4E1', '#F5DEB3', '#FFDEAD', '#D3D3D3', '#FFFFFF'
];

const ColorPalette: React.FC<ColorPaletteProps> = ({
    selectedColor, 
    onColorSelect, 
    colorScheme,
    completedColors = {}
}) => {
    const [customizingButtonIndex, setCustomizingButtonIndex] = useState<number | null>(null);
    const isEditMode = customizingButtonIndex !== null;
    const isCustomPickerVisible = customizingButtonIndex !== null;
    const fixedColorScheme = colorScheme && colorScheme.length > 0;
    const [colors, setColors] = useState<string[]>(fixedColorScheme ? colorScheme.slice(0, 14) : defaultColors.slice(0, 14));
    const [tempColor, setTempColor] = useState(selectedColor);

    const longPressTimer = useRef<NodeJS.Timeout>();
    const isLongPressing = useRef(false);
    const {vibrate} = useVibration();
    const isMobile = useIsMobile();

    useEffect(() => {
        if (!fixedColorScheme) {
            const loadSavedColors = async () => {
                try {
                    const savedColors = await PersistentManager.getInstance().getItem(STORAGE_KEY);
                    if (savedColors) {
                        const list = JSON.parse(savedColors);
                        setColors(list.slice(0, 14));
                    }
                } catch (error) {
                    console.error('Error loading saved colors:', error);
                }
            };

            loadSavedColors();
        }
    }, [fixedColorScheme]);

    useEffect(() => {
        if (!fixedColorScheme) {
            PersistentManager.getInstance().setItem(STORAGE_KEY, JSON.stringify(colors));
        }
    }, [colors, colorScheme, fixedColorScheme]);

    const handleColorSelect = (color: string, index: number) => {
        if (completedColors[color]) {
            return; // Don't allow selection of completed colors
        }
        
        // Track color selection
        trackColorSelection(color, index);
        
        if (PLAY_SOUND) {
            playColorSelectSound();
        }
        vibrate('select');
        
        onColorSelect(color, color);
    };

    const handleLongPress = useCallback((index: number) => {
        // Don't allow color customization if using a fixed color scheme
        if (fixedColorScheme) return;

        isLongPressing.current = true;
        vibrate('longPress');
        setCustomizingButtonIndex(index);
        setTempColor(colors[index]);
    }, [colors, vibrate, fixedColorScheme]);

    const handleMouseDown = (index: number, e: React.MouseEvent) => {
        // Don't initiate long press if using fixed color scheme
        if (fixedColorScheme) return;

        if (e.button === 0 || e.button === 2) {
            e.preventDefault();
            longPressTimer.current = setTimeout(() => {
                handleLongPress(index);
            }, 500);
        }
    };

    const handleMouseUp = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
        }
        isLongPressing.current = false;
    };

    const handleTouchStart = (index: number) => {
        // Don't initiate long press if using fixed color scheme
        if (fixedColorScheme) return;

        longPressTimer.current = setTimeout(() => {
            handleLongPress(index);
        }, 500);
    };

    const handleTouchEnd = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
        }
        isLongPressing.current = false;
    };

    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
    };

    const handleCustomColorChange = (color: string) => {
        if (customizingButtonIndex !== null) {
            setTempColor(color);
        }
    };

    const handleCustomColorCancel = () => {
        vibrate('success');
        setCustomizingButtonIndex(null);
        isLongPressing.current = false;
    };

    const handleCustomColorSave = () => {
        trackUserInteraction('custom_color_save', {
            color: tempColor,
            button_index: customizingButtonIndex
        });
        
        if (customizingButtonIndex !== null) {
            const newColors = [...colors];
            newColors[customizingButtonIndex] = tempColor;
            setColors(newColors);
            
            if (!fixedColorScheme) {
                PersistentManager.getInstance().setItem(STORAGE_KEY, JSON.stringify(newColors));
            }
            
            setCustomizingButtonIndex(null);
            onColorSelect(tempColor, tempColor);
        }
    };

    const getSelectedIndex = (): number => {
        return colors.findIndex(x => x === selectedColor);
    };

    const handleSetupClick = () => {
        vibrate('tap');
        setCustomizingButtonIndex(!isEditMode ? getSelectedIndex() : null);
    };

    const handleEraserClick = () => {
        trackUserInteraction('eraser_selected', {
            from_component: 'color_palette'
        });
        onColorSelect('eraser', 'eraser');
    };

    return (
        <PaletteContainer onContextMenu={handleContextMenu}>
            <ColorRow className="color-palette-scroll" isMobile={isMobile}>
                {colors.map((color, index) => {
                    const isCompleted = colorScheme !== undefined && completedColors[index+1];
                    const isSelected = selectedColor === color;
                    
                    return (
                        <ColorButtonWrapper key={`color-${index}`}>
                            <ColorButton
                                color={color}
                                isSelected={isSelected}
                                isEditMode={customizingButtonIndex === index}
                                isCompleted={!fixedColorScheme && isCompleted}
                                onClick={() => handleColorSelect(color, index)}
                                onMouseDown={(e) => handleMouseDown(index, e)}
                                onMouseUp={handleMouseUp}
                                onMouseLeave={handleMouseUp}
                                onTouchStart={() => handleTouchStart(index)}
                                onTouchEnd={handleTouchEnd}
                                onContextMenu={handleContextMenu}
                                className={isCompleted ? "completed" : ""}
                            >
                                {fixedColorScheme && !isCompleted && (
                                    <SelectedOverlay>{index+1}</SelectedOverlay>
                                )}
                                {isCompleted && (
                                    <SelectedOverlay className="completed">✓</SelectedOverlay>
                                )}
                                {/*{!fixedColorScheme && isCompleted && !isSelected && (*/}
                                {/*    <SelectedOverlay>✓</SelectedOverlay>*/}
                                {/*)}*/}
                            </ColorButton>
                        </ColorButtonWrapper>
                    );
                })}
                {!fixedColorScheme && (
                    <>
                        <ColorButtonWrapper key="eraser">
                            <ColorActionButton
                                color="#ccc"
                                isSelected={selectedColor === 'eraser'}
                                isEditMode={false}
                                onClick={handleEraserClick}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="transparent" stroke="black">
                                    <rect x="8" y="3" width="10" height="15" fill="gray" transform="rotate(45 10 10)" />
                                </svg>
                            </ColorActionButton>
                        </ColorButtonWrapper>
                        <ColorButtonWrapper key="setup">
                            <ColorActionButton
                                color="#ccc"
                                isSelected={false}
                                isEditMode={false}
                                onClick={handleSetupClick}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="transparent" stroke="black">
                                    <path d="M12 2C6.48 2 2 6.46 2 11.94c0 3.31 1.8 6.23 4.5 7.96.51.34.78.94.67 1.55l-.38 2.14c-.1.58.5 1.01.99.75l3.34-1.67a2 2 0 0 1 1.78 0l3.34 1.67c.49.26 1.09-.17.99-.75l-.38-2.14a1.7 1.7 0 0 1 .67-1.55A9.99 9.99 0 0 0 22 11.94C22 6.46 17.52 2 12 2Zm-5 10a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm3-4a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm4 4a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm3-4a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Z"/>
                                </svg>
                            </ColorActionButton>
                        </ColorButtonWrapper>
                    </>
                )}
            </ColorRow>
            {isCustomPickerVisible && (
                <ColorPickerOverlay>
                    <ColorPickerHeader>
                        <ColorPickerTitle>Choose Color</ColorPickerTitle>
                        <ColorPickerButtonsPanel>
                            <CloseButton onClick={handleCustomColorCancel}>Close</CloseButton>
                        </ColorPickerButtonsPanel>
                    </ColorPickerHeader>
                    <ColorPickerContainer>
                        <ColorPicker
                            value={tempColor}
                            onChange={handleCustomColorChange}
                            hideControls
                            hidePresets
                            hideEyeDrop
                            hideColorTypeBtns
                            hideGradientType
                            hideGradientAngle
                            hideGradientStop
                            hideInputs
                            width={300}
                        />
                    </ColorPickerContainer>
                    <CloseButton onClick={handleCustomColorSave}>✓</CloseButton>
                </ColorPickerOverlay>
            )}
        </PaletteContainer>
    );
};

export default ColorPalette;